import { useCallback } from 'react';

import {
    getChangedLaunchTypes,
    getChangedOrphanMilestones,
    shouldAutosaveRecurrence,
} from './deadlineAutosave';
import { deriveDaysAllowedToComplete, LAUNCH_MILESTONE_BY_TYPE } from './milestones';
import {
    DeadlinesSaveOutcome,
    findSaveRejection,
    getDeadlinesSaveFailureMessageKey,
    summariseDeadlinesSave,
} from './savingOutcome';
import { getDeadlinesBlockingMessage } from './validation';

import type { MilestoneReminderValues } from './components/MilestoneCard/MilestoneCard';
import type { RemindersById } from './useHydrateDeadlineValues';
import type { DeadlineErrorsById, DeadlineMilestoneValues } from './validation';
import type { useGetCycleEvaluation } from '@/api/cycle-evaluation-services/cycle-evaluation';
import type { EvaluationTypeValue } from '@/constants/cycle-evaluation/CycleEvaluation';
import type { FrequencyValue } from '@/constants/Frequency';
import type { CycleEvaluationGroup } from '@/types/cycle-evaluation/CycleEvaluationGroup';
import type { CycleMilestone } from '@/types/cycle-evaluation/CycleMilestone';
import type { ScheduleReminder } from '@/types/cycle-evaluation/CycleSchedule';

import { useUpdateCycleEvaluationGroup } from '@/api/cycle-evaluation-services/cycle-evaluation-group/cycleEvaluationGroupMutations';
import {
    useCreateCycleMilestone,
    useUpdateCycleMilestone,
} from '@/api/cycle-evaluation-services/cycle-milestone';
import { useUpdateCycleSchedule } from '@/api/cycle-evaluation-services/cycle-schedule';
import { CycleEvaluationGroupSteps } from '@/constants/cycle-evaluation/CycleEvaluationGroupSteps';
import { useAmelioTranslations } from '@/hooks';
import { useError } from '@/hooks/utils/useError';
import { buildCycleEvaluationGroupUpdatePayload } from '@/navigation/CycleEvaluation/cycleEvaluationGroupSteps';
import { showWarningToast } from '@/plugins/Toast';
import { mergeScheduleReminders } from '@/utils/schedule';

// Delegates to the merge the 360 and the legacy cycle schedule page already ship: a reminder
// outside the offered 7/3/0 set — a value stored before that scale existed — is carried over
// as-is, never dropped just because this step's own checkboxes don't offer it.
const buildScheduleReminders = ({
    storedReminders,
    reminders,
}: {
    storedReminders: readonly ScheduleReminder[];
    reminders?: MilestoneReminderValues;
}): ScheduleReminder[] =>
    mergeScheduleReminders({
        storedReminders: [...storedReminders],
        reminderSevenDays: reminders?.reminderSevenDays,
        reminderThreeDays: reminders?.reminderThreeDays,
        reminderOnDeadline: reminders?.reminderOnDeadline,
    });

interface UseSaveDeadlinesParams {
    isReadOnly: boolean;
    /** The cycle is no longer a draft: see `isCycleEvaluationGroupLaunched`. */
    isLaunched: boolean;
    /** At least one field of the step was touched since it was opened. */
    hasUserEdited: boolean;
    errors: DeadlineErrorsById;
    group: CycleEvaluationGroup | undefined;
    cycleEvaluationGroupId: string | undefined;
    values: DeadlineMilestoneValues;
    orphanMilestones: CycleMilestone[];
    checkedTypes: EvaluationTypeValue[];
    cycleEvaluationQueryByType: Record<
        EvaluationTypeValue,
        ReturnType<typeof useGetCycleEvaluation>
    >;
    remindersById: RemindersById;
    frequency?: FrequencyValue;
}

/**
 * Builds the Deadlines step's "Next" action: validates, then writes the orphan milestones and the
 * checked types' launch schedules whose value differs from the stored one, and the group's
 * `currentStep` while the cycle is still a draft — pulled out of `DeadlinesPage` so the save
 * orchestration can be read on its own, away from the hydration effect.
 *
 * A launched cycle nobody has touched passes straight through: nothing is validated and nothing is
 * written, so the administrator can browse every step of a live cycle whose dates have gone past.
 */
const useSaveDeadlines = ({
    isReadOnly,
    isLaunched,
    hasUserEdited,
    errors,
    group,
    cycleEvaluationGroupId,
    values,
    orphanMilestones,
    checkedTypes,
    cycleEvaluationQueryByType,
    remindersById,
    frequency,
}: UseSaveDeadlinesParams) => {
    const { t } = useAmelioTranslations();
    const { handleError } = useError();

    const { mutateAsync: createCycleMilestone } = useCreateCycleMilestone();
    const { mutateAsync: updateCycleMilestone } = useUpdateCycleMilestone();
    const { mutateAsync: updateCycleSchedule } = useUpdateCycleSchedule();
    const { mutateAsync: updateCycleEvaluationGroup } = useUpdateCycleEvaluationGroup();

    const onNext = useCallback(async () => {
        // Backstop — completed cycles must refuse writes from any entry point.
        if (isReadOnly) {
            return false;
        }

        // Untouched, a launched cycle has nothing to save and nothing to refuse — its dates have
        // naturally gone past, and blocking here would lock the steps that follow.
        if (isLaunched && !hasUserEdited) {
            return true;
        }

        const blockingKey = getDeadlinesBlockingMessage({ errors, frequency });

        if (blockingKey) {
            showWarningToast({ message: t(blockingKey) });

            return false;
        }

        if (!group || !cycleEvaluationGroupId) {
            return false;
        }

        try {
            // Only what differs from the stored value leaves: the autosave has usually written the
            // edit already, and re-sending an untouched milestone is a write nobody asked for.
            const changedOrphanMilestones = getChangedOrphanMilestones({
                values,
                orphanMilestones,
            });

            const orphanWrites = changedOrphanMilestones.flatMap((milestone) => {
                const value = values[milestone.id];
                const existing = orphanMilestones.find(
                    (row) => row.milestoneType === milestone.milestoneType,
                );
                const scheduledAt = value?.scheduledAt ? value.scheduledAt.toISOString() : null;

                if (existing) {
                    return [updateCycleMilestone({ id: existing.id, payload: { scheduledAt } })];
                }

                return scheduledAt
                    ? [
                          createCycleMilestone({
                              cycleEvaluationGroupId,
                              milestoneType: milestone.milestoneType,
                              scheduledAt,
                          }),
                      ]
                    : [];
            });

            let results: PromiseSettledResult<unknown>[] = await Promise.allSettled(orphanWrites);

            if (summariseDeadlinesSave(results) === DeadlinesSaveOutcome.SAVED) {
                const changedLaunchTypes = getChangedLaunchTypes({
                    values,
                    checkedTypes,
                    remindersById,
                    cycleEvaluationQueryByType,
                });

                const scheduleWrites = checkedTypes.flatMap((type) => {
                    const definition = LAUNCH_MILESTONE_BY_TYPE[type];
                    const value = values[definition.id];
                    const schedule = cycleEvaluationQueryByType[type]?.data?.cycleSchedule;

                    if (!schedule || !changedLaunchTypes.includes(type)) {
                        return [];
                    }

                    const scheduledAt = value?.scheduledAt ? value.scheduledAt.toISOString() : null;
                    const daysAllowedToComplete =
                        value?.scheduledAt && value.dueAt
                            ? deriveDaysAllowedToComplete({
                                  scheduledAt: value.scheduledAt,
                                  dueAt: value.dueAt,
                              })
                            : schedule.daysAllowedToComplete;
                    // A type that never hydrated holds no choice to send: it keeps what the server has,
                    // so a write made for a DATE cannot carry an emptying nobody asked for.
                    const scheduleReminders = remindersById[definition.id]
                        ? buildScheduleReminders({
                              storedReminders: schedule.scheduleReminders,
                              reminders: remindersById[definition.id],
                          })
                        : schedule.scheduleReminders;

                    return [
                        updateCycleSchedule({
                            ...schedule,
                            scheduledAt,
                            daysAllowedToComplete,
                            scheduleReminders,
                        }),
                    ];
                });

                results = [...results, ...(await Promise.allSettled(scheduleWrites))];
            }

            // A draft always moves its resume point forward. A launched cycle has no resume point
            // left to move, so its group is written only when the recurrence itself changed.
            const shouldWriteGroup = !isLaunched || shouldAutosaveRecurrence({ frequency, group });

            if (
                summariseDeadlinesSave(results) === DeadlinesSaveOutcome.SAVED &&
                shouldWriteGroup
            ) {
                results = [
                    ...results,
                    ...(await Promise.allSettled([
                        updateCycleEvaluationGroup({
                            id: cycleEvaluationGroupId,
                            payload: buildCycleEvaluationGroupUpdatePayload({
                                group,
                                currentStep: isLaunched
                                    ? undefined
                                    : CycleEvaluationGroupSteps.COMMUNICATIONS,
                                frequency,
                            }),
                        }),
                    ])),
                ];
            }

            const outcome = summariseDeadlinesSave(results);
            const failureMessageKey = getDeadlinesSaveFailureMessageKey(outcome);

            if (!failureMessageKey) {
                return true;
            }

            handleError({
                error: findSaveRejection(results),
                data: { title: t('errors.genericTitle'), message: t(failureMessageKey) },
            });

            return false;
        } catch (error) {
            handleError({ error });

            return false;
        }
    }, [
        isReadOnly,
        isLaunched,
        hasUserEdited,
        errors,
        frequency,
        t,
        group,
        cycleEvaluationGroupId,
        values,
        orphanMilestones,
        checkedTypes,
        cycleEvaluationQueryByType,
        remindersById,
        createCycleMilestone,
        updateCycleMilestone,
        updateCycleSchedule,
        updateCycleEvaluationGroup,
        handleError,
    ]);

    return onNext;
};

export { useSaveDeadlines, buildScheduleReminders };
